import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import { formatTimeAgo } from "../utils/timeHelpers";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEdit,
  faTrash,
  faToggleOn,
  faToggleOff,
  faUserTie,
  faKey, faSortUp, faSortDown
} from "@fortawesome/free-solid-svg-icons";
import { AdminAccessAlertContext } from "../contexts/AdminAccessAlertContext";
import { RateLimitAlertContext } from "../contexts/RateLimitAlertContext";


const UserRoleDataTablePage_v2 = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [columns, setColumns] = useState([
    { key: "role_id", label: "Role ID", sortable: true, filterable: true },
    { key: "role_name", label: "Role Name", sortable: true, filterable: true },
    { key: "description", label: "Description", sortable: true, filterable: true },
    { key: "created_at", label: "Created At", sortable: true, filterable: false },
    { key: "updated_at", label: "Updated At", sortable: true, filterable: false },
    { key: "actions", label: "Actions", sortable: false, filterable: false },
  ]);
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: "role_id",
    direction: "asc",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Rename the functions to avoid conflicts
  const { openAlert: openRateLimitAlert } = useContext(RateLimitAlertContext);
  const { openAlert: openAdminAccessAlert } = useContext(AdminAccessAlertContext);

  useEffect(() => {
    fetchData();
  }, [filters, sortConfig, currentPage, itemsPerPage]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/admin/roles`, {
        params: {
          page: currentPage,
          per_page: itemsPerPage,
          sort_by: sortConfig.key,
          sort_direction: sortConfig.direction,
          ...filters,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
        },
      });
      setData(response.data.data);
      setTotalPages(response.data.last_page);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        window.location.href = "/login";
      }
      if (error.response?.status === 403) {
        console.log("403 error")
        openAdminAccessAlert()
      }
      if (error.response?.status === 429) {
        console.log("403 error")
        openRateLimitAlert()
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleFilterChange = (key, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value,
    }));
    setCurrentPage(1); // Reset to the first page when applying filters
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1); // Reset to the first page when changing items per page
  };

  const handleDelete = async (roleId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${BASE_URL}/admin/roles/${roleId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          },
        });
        Swal.fire("Deleted!", "The role has been deleted.", "success");
        fetchData();
      } catch (error) {
        Swal.fire("Error!", "Failed to delete the role.", "error");
      }
    }
  };

  const handleEdit = (roleId) => {
    navigate(`/roles/edit/${roleId}`);
  };

  const handleViewPermissions = (roleId) => {
    navigate(`/roles/permissions/${roleId}`);
  };

  const renderPageNumbers = () => {
    const pages = [];
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (currentPage <= 3) {
      endPage = Math.min(5, totalPages);
    } else if (currentPage >= totalPages - 2) {
      startPage = Math.max(totalPages - 4, 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 mx-1 border rounded text-sm ${
            currentPage === i
              ? "bg-blue-500 text-white"
              : "bg-white text-gray-700"
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  const TableSkeleton = ({ columns }) => {
    return (
      <>
        {Array.from({ length: 6 }).map((_, index) => (
          <tr key={index} className="hover:bg-gray-50">
            {columns.map((column) => (
              <td key={column.key} className="px-4 py-2 text-sm text-gray-700">
                <div className="h-4 bg-gray-300 rounded animate-pulse"></div>
              </td>
            ))}
          </tr>
        ))}
      </>
    );
  };

  return (
    <div className="overflow-x-auto bg-white shadow-md rounded-lg p-4">
      <table className="min-w-full border border-gray-300 rounded-lg overflow-hidden">
        <thead className="bg-gray-100">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className="px-4 py-2 text-left text-sm font-medium text-gray-600 uppercase cursor-pointer border-b border-gray-300"
                onClick={() => column.sortable && handleSort(column.key)}
              >
                {column.label}
                {column.sortable && sortConfig.key === column.key && (
                  <span className="ml-1">
                    {sortConfig.direction === "asc" ? (
                      <FontAwesomeIcon icon={faSortUp} />
                    ) : (
                      <FontAwesomeIcon icon={faSortDown} />
                    )}
                  </span>
                )}
              </th>
            ))}
          </tr>
          <tr>
            {columns.map((column) => (
              <td key={column.key} className="px-4 py-2">
                {column.filterable && (
                  <input
                    type="text"
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder={`Filter ${column.label}`}
                    value={filters[column.key] || ""}
                    onChange={(e) =>
                      handleFilterChange(column.key, e.target.value)
                    }
                  />
                )}
              </td>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <TableSkeleton columns={columns} />
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-2 text-center">
                No data available.
              </td>
            </tr>
          ) : (
            data.map((row) => (
              <tr key={row.role_id} className="hover:bg-gray-50">
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className="px-4 py-2 text-sm text-gray-700 border-b border-gray-300"
                  >
                    {column.key === "created_at" || column.key === "updated_at"
                      ? formatTimeAgo(row[column.key])
                      : column.key === "actions" ? (
                        <div className="flex space-x-2 text-lg">
                          <button
                            onClick={() => handleEdit(row.role_id)}
                            className="text-blue-500 hover:text-blue-700"
                            title="Edit"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button
                            onClick={() => handleDelete(row.role_id)}
                            className="text-red-500 hover:text-red-700"
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                          <button
                            onClick={() => handleViewPermissions(row.role_id)}
                            className="text-purple-500 hover:text-purple-700"
                            title="View Permissions"
                          >
                            <FontAwesomeIcon icon={faKey} />
                          </button>
                        </div>
                      ) : (
                        row[column.key]
                      )}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
  
      {/* Pagination Controls */}
      <div className="flex justify-between items-center mt-4">
        <div>
          <select
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
            className="px-2 py-1 border border-gray-300 rounded text-sm"
          >
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>
        <div className="flex items-center">
          <button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1 || loading}
            className="px-3 py-1 mx-1 border border-gray-300 rounded bg-white text-gray-700 disabled:opacity-50"
            title="First"
          >
            First
          </button>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1 || loading}
            className="px-3 py-1 mx-1 border border-gray-300 rounded bg-white text-gray-700 disabled:opacity-50"
            title="Previous"
          >
            Previous
          </button>
          {renderPageNumbers()}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || loading}
            className="px-3 py-1 mx-1 border border-gray-300 rounded bg-white text-gray-700 disabled:opacity-50"
            title="Next"
          >
            Next
          </button>
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages || loading}
            className="px-3 py-1 mx-1 border border-gray-300 rounded bg-white text-gray-700 disabled:opacity-50"
            title="Last"
          >
            Last
          </button>
        </div>
      </div>
    </div>
  );
  
  
};

export default UserRoleDataTablePage_v2;